/** Controller for the usage-tier account states: load → render → transactions.
 *
 * Layer: `controller` per docs/design/spa-account-extraction-260524.md §3.3.
 *
 * Covers:
 *   - loadAccount          — fetch /api/account, render, fire loadTokenTransactions
 *   - renderAccount        — switch on AccountState → innerHTML, then loadApiTokens
 *   - loadTokenTransactions — fetch /api/account/token-transactions
 *
 * Cross-controller note: renderAccount needs to call loadApiTokens from
 * controller-api-tokens.ts. Since the SPA_ACCOUNT_ALLOW matrix does not
 * allow peer-controller imports, this is solved by accepting a
 * `loadApiTokensFn: () => Promise<void>` callback injected from init.ts
 * (the orchestrator layer which CAN import all controllers).
 *
 * Extracted from web/static/account.html lines 395-502 in Phase AC.5.
 */
import { pickAccountState, userAdminsAnyGroup } from './model.js';
import { renderAnonymousState, renderIndividualState, renderCapHitState, } from './view-usage.js';
import { renderFlatState, renderInvoicedState } from './view-subscription.js';
import { renderGroupAdminCta } from './view-groups.js';
import { renderDangerZone, renderTokenTxTable } from './view-recent-signups.js';
import { renderApiTokensPlaceholder } from './view-api-tokens.js';
export function createUsageController(deps) {
    const { esc, authFetch, fetch: fetchFn, doc, loadApiTokensFn } = deps;
    async function loadAccount() {
        try {
            let resp = await authFetch('/api/account');
            if (resp.status === 401)
                resp = await fetchFn('/api/account');
            if (resp.status === 401) {
                const loading = doc.getElementById('loading-indicator');
                if (loading)
                    loading.style.display = 'none';
                const notLoggedIn = doc.getElementById('not-logged-in');
                if (notLoggedIn)
                    notLoggedIn.style.display = '';
                return;
            }
            if (!resp.ok)
                throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json();
            renderAccount(data);
            // Fire-and-forget — token-transactions panel handles its own
            // 404 / error states.
            void loadTokenTransactions();
        }
        catch (_e) {
            const loading = doc.getElementById('loading-indicator');
            if (loading)
                loading.style.display = 'none';
            const banner = doc.getElementById('error-banner');
            if (banner) {
                banner.textContent = 'Failed to load account info. Please try again.';
                banner.style.display = '';
            }
        }
    }
    function renderAccount(data) {
        const loading = doc.getElementById('loading-indicator');
        if (loading)
            loading.style.display = 'none';
        const main = doc.getElementById('account-content');
        if (!main)
            return;
        main.style.display = '';
        const state = pickAccountState(data);
        const adminsGroup = userAdminsAnyGroup(data);
        let html = '<h1 class="account-title">My Account</h1>';
        switch (state) {
            case 'anonymous':
                html += renderAnonymousState(esc, data);
                break;
            case 'individual':
                html += renderIndividualState(esc, data);
                break;
            case 'institutional-flat':
                html += renderFlatState(esc, data);
                break;
            case 'institutional-invoiced':
                html += renderInvoicedState(esc, data);
                break;
            case 'cap-hit':
                html += renderCapHitState(esc, data);
                break;
        }
        // API Tokens section: every signed-in state.
        if (state !== 'anonymous')
            html += renderApiTokensPlaceholder();
        if (adminsGroup)
            html += renderGroupAdminCta(esc, data);
        // Danger Zone always appears for signed-in users.
        if (state !== 'anonymous')
            html += renderDangerZone();
        main.innerHTML = html;
        main.dataset['state'] = state;
        // Fire-and-forget — the token-list panel handles its own error states.
        if (state !== 'anonymous')
            void loadApiTokensFn();
    }
    async function loadTokenTransactions() {
        const body = doc.getElementById('token-tx-body');
        if (!body)
            return;
        try {
            const resp = await authFetch('/api/account/token-transactions?limit=20');
            if (resp.status === 404) {
                body.innerHTML = '<p style="font-size:12px;color:var(--text-muted)">'
                    + 'Transactions API not yet wired. Check back shortly.</p>';
                return;
            }
            if (!resp.ok) {
                body.innerHTML = '<p style="font-size:12px;color:var(--error)">'
                    + 'Failed to load transactions (HTTP ' + resp.status + ').</p>';
                return;
            }
            const data = await resp.json();
            const tx = Array.isArray(data.transactions) ? data.transactions : [];
            if (tx.length === 0) {
                body.innerHTML = '<p style="font-size:12px;color:var(--text-muted)">'
                    + 'No credit activity yet. Credits are consumed when you remediate a file.</p>';
                return;
            }
            const pricing = window.adatool?.pricing;
            // PricingConfig has optional pdfCreditsPerPage/officeCreditsPerPage;
            // cast to the narrower PricingMultipliers shape that renderTokenTxTable accepts.
            body.innerHTML = renderTokenTxTable(esc, tx, pricing);
        }
        catch (_e) {
            window.adatool?.log?.warn('[account] token-transactions fetch failed:', _e);
            body.innerHTML = '<p style="font-size:12px;color:var(--text-muted)">'
                + 'Could not load transactions. Refresh to retry.</p>';
        }
    }
    return { loadAccount, renderAccount, loadTokenTransactions };
}
